'use client';

/**
 * API namespace `hubCrm` — Hub CRM dashboard client.
 *
 * Spec ref: docs/SPEC-ANALYTICS-CRM-HUB.md §3.1 (GET /api/users/me/hub-crm)
 *           + §3.2 (POST /api/public/hub-visit beacon).
 *           PR2 §2.2 (this commit) — devices + geo + FOMO wall NICE-TO-HAVE.
 *
 * Backend dependency (PR1a, commit 76af5f6391, PR #1252):
 *   - GET /api/users/me/hub-crm?range=7d|30d|90d  (requireAuth → { data: HubCrmData })
 *   - POST /api/public/hub-visit                 (public, 204 always, fire-and-forget)
 *   PR2: GET /api/users/me/hub-crm now returns extended HubCrmData with
 *   devicesBreakdown + geoBreakdown + recentVisits (additive fields, backward
 *   compatible with PR1b frontend consumers that don't read them yet).
 *
 * Anti-P-001 (PII compliance): the beacon payload contains ONLY
 *   { hubOwnerUsername, referer?, ctaClicked? } — NO IP addresses, NO JWTs,
 *   NO emails, NO user ids. The backend hashes the visitor IP separately
 *   (see backend hashVisitorIp). The frontend never touches raw PII.
 *   PR2: `country` returned in geoBreakdown is country-granularity only
 *   (NOT city) — per SPEC §9.5 country is aggregate analytics, NOT PII.
 *
 * Anti-P-041: 0 unsafe any-casts. `getHubCrmData` uses `apiFetch<HubCrmData>`
 *   which auto-unwraps the `{ data: HubCrmData }` envelope (see
 *   fetch-client.ts handleResponse). No assertion needed. `getRecentVisits`
 *   delegates to `getHubCrmData` + extracts `.recentVisits` — same envelope
 *   handling, no separate endpoint needed (per controller §4 spec note).
 *
 * Anti-P-042: 0 console.* calls. The beacon's `.catch(() => {})` silently
 *   swallows errors (analytics must NEVER break the visitor UX).
 */

import { apiFetch } from '../core/fetch-client';

// ─── Types (mirror backend/src/services/hub-crm.service.ts HubCrmData) ───────

export type HubCrmRange = '7d' | '30d' | '90d';

export interface HubCrmTopCta {
  /** CTA identifier (e.g. 'planCompensacion', 'myMarket', 'whatsappDirect'). */
  cta: string;
  /** Absolute click count within the selected range. */
  count: number;
  /** % of CTA clicks attributable to this CTA (0-100). */
  percentage: number;
}

export interface HubCrmTopReferrer {
  /** Referer URL (truncated on the backend) or null for direct traffic. */
  referer: string | null;
  /** Visit count from this referer within the selected range. */
  count: number;
}

/**
 * PR2 §2.2 — devices breakdown. 3 buckets parsed by the backend beacon
 * middleware from the visitor's User-Agent. The FOMO wall does NOT show
 * device info — this is used by the DevicesGeoCard pie chart only.
 */
export interface HubCrmDevicesBreakdown {
  mobile: number;
  desktop: number;
  tablet: number;
}

/**
 * PR2 §2.2 — geo breakdown row. `country` is ISO 3166-1 alpha-2 (e.g. 'US',
 * 'ES', 'MX') or null when no CDN edge header was present. Frontend renders
 * null as "Desconocido" (per SPEC §9.5 — country granularity is aggregate,
 * not PII).
 */
export interface HubCrmGeoRow {
  country: string | null;
  count: number;
}

/**
 * PR2 §2.2 — recent visit row for the FOMO wall. Only exposes visitedAt +
 * referer + ctaClicked — the backend NEVER returns country/deviceType/UA/
 * visitorIpHash via this surface (per HubCrmRecentVisit backend docstring —
 * minimum-data principle for the social-proof FOMO wall surface).
 *
 * `visitedAt` is serialized by Express/JSON.stringify as an ISO 8601 string
 * (e.g. '2024-12-15T10:30:00.000Z') — typed as `string` on the client.
 */
export interface HubCrmRecentVisit {
  visitedAt: string;
  referer: string | null;
  ctaClicked: string | null;
}

/**
 * PR3 §3.1 — benchmarking tier card data (mirrors backend
 * `HubCrmTierBenchmark` interface in backend/src/services/hub-crm.service.ts,
 * Sub-PR 2 commit 9b72914e). Returned by `getTierBenchmark(userId, range)`
 * which aggregates the `HubVisit` collection by `hubOwnerTier` (Sub-PR 1
 * `tier_benchmark` index, commit ff76b9f5) and returns the visiting user's
 * rank vs the median of their tier peers.
 *
 * `tier` is the user's current package tier (one of the 7 enum values from
 * `sub-schemas/package.ts`); the benchmark is null when the user is on
 * `free` tier, has no package, has an expired tier, or has fewer than 10
 * peers in the same tier (Founder decision §5.6 — minimum sample).
 *
 * `hasMinSample` is true when `userCount >= 10` (per Founder decision
 * 2026-10-08). The card renders a "Datos insuficientes" hint when false.
 *
 * `myConversionRate` + `medianConversionRate` are percentages 0-100.
 * `myRank` is 1-indexed (1 = most visits in tier); `totalRank` equals
 * `userCount` (the total number of paid-tier peers).
 */
export interface HubCrmTierBenchmark {
  tier: 'free' | 'basic' | 'bronce' | 'plata' | 'oro' | 'platino' | 'diamante';
  userCount: number;
  hasMinSample: boolean;
  myVisits: number;
  myConversionRate: number;
  medianVisits: number;
  medianConversionRate: number;
  myRank: number;
  totalRank: number;
}

/**
 * PR3 §3.1 — Hub→Market conversion card data (mirrors backend
 * `HubToMarketConversion` interface in backend/src/services/hub-crm.service.ts,
 * Sub-PR 2 commit 9b72914e). Returned by `getHubToMarketConversion(userId,
 * range)`.
 *
 * - `visitsTotal` = total HubVisit count in the selected range (same value
 *   as `HubCrmData.totalVisits` — duplicated here for the conversion card's
 *   self-contained rendering).
 * - `marketClicks` = HubVisit count in range with `ctaClicked === 'myMarket'`
 *   (the new enum value added by Sub-PR 1 — commit ff76b9f5 — recorded by the
 *   `sendHubVisitBeacon({ ctaClicked: 'myMarket' })` fire-and-forget call in
 *   `visit-market-button.tsx`, Sub-PR 3).
 * - `conversionRate` = `marketClicks / visitsTotal * 100` (0-100, guarded
 *   against /0 on the backend → 0 when no visits).
 */
export interface HubToMarketConversion {
  visitsTotal: number;
  marketClicks: number;
  conversionRate: number;
}

export interface HubCrmData {
  /** Total visits in the selected range. */
  totalVisits: number;
  /** Visits since UTC midnight today (independent of range). */
  visitsToday: number;
  /** 7-element array of visit counts for the last 7 UTC days (oldest → newest). */
  weeklyTrend: number[];
  /** % of range visits where a CTA was clicked: count(ctaClicked != null) / total * 100. */
  ctaConversionRate: number;
  /** CTAs ranked by click count within the range, with per-CTA % of CTA clicks. */
  topCtas: HubCrmTopCta[];
  /** Top 5 referer sources by visit count within the range. */
  topReferrers: HubCrmTopReferrer[];
  /** PR2 §2.2 — devices breakdown (mobile/desktop/tablet counts in range). */
  devicesBreakdown: HubCrmDevicesBreakdown;
  /** PR2 §2.2 — top 5 countries by visit count in range. */
  geoBreakdown: HubCrmGeoRow[];
  /** PR2 §2.2 — last 5 visits for FOMO wall (most recent first). */
  recentVisits: HubCrmRecentVisit[];
  /**
   * PR3 §3.1 — benchmarking tier card data. `null` when the user is on
   * `free` tier, has no package, has an expired tier, or has fewer than 10
   * peers in the same tier (the card renders a graceful "Datos insuficientes"
   * state in that case — never an error).
   */
  tierBenchmark: HubCrmTierBenchmark | null;
  /**
   * PR3 §3.1 — Hub→Market conversion card data. Always populated (no null
   * case — `conversionRate` is 0 when there are no visits or no market
   * clicks, per backend guard).
   */
  hubToMarketConversion: HubToMarketConversion;
}

// ─── GET /api/users/me/hub-crm?range= ────────────────────────────────────────

/**
 * Fetch the Hub CRM dashboard aggregate for the authenticated user.
 *
 * The backend wraps the response in `{ data: HubCrmData }`; `apiFetch`
 * auto-unwraps the `.data` envelope (see fetch-client.ts handleResponse),
 * so this function returns `HubCrmData` directly.
 *
 * PR2: the response now ALSO carries devicesBreakdown + geoBreakdown +
 * recentVisits (additive — backward compatible with PR1b consumers that
 * don't read these fields).
 *
 * @param range '7d' | '30d' | '90d' (default '30d'). Any other value is
 *              rejected by the backend with 400 VALIDATION_ERROR.
 */
export async function getHubCrmData(
  range: HubCrmRange = '30d',
): Promise<HubCrmData> {
  return apiFetch<HubCrmData>(`/users/me/hub-crm?range=${range}`);
}

// ─── POST /api/public/hub-visit beacon ────────────────────────────────────────

export interface HubVisitBeaconPayload {
  /** Hub owner's username — identifies which Hub was visited. */
  hubOwnerUsername: string;
  /** Referer URL (document.referrer) — null/undefined for direct traffic. */
  referer?: string;
  /** CTA identifier if the visit followed a CTA click (optional). */
  ctaClicked?: string;
}

/**
 * Fire-and-forget beacon to record a Hub visit on the public Profileia Hub
 * page (`/{username}`). Uses `keepalive: true` so the beacon survives page
 * navigation / unload. Errors are silently swallowed (analytics must NEVER
 * break the visitor UX — see SPEC §8 mitigation + P-001).
 *
 * Anti-P-001: payload contains ONLY { hubOwnerUsername, referer?, ctaClicked? }.
 * NO IP, NO JWT, NO email is ever sent from the frontend.
 */
export function sendHubVisitBeacon(payload: HubVisitBeaconPayload): void {
  // The relative URL works because Next.js rewrites /api/* to the backend
  // (next.config.ts or env-configured proxy in production). If the rewrite
  // is missing, the fetch fails silently — analytics is non-critical.
  const doFetch = typeof fetch === 'function' ? fetch : null;
  if (!doFetch) return;
  void doFetch('/api/public/hub-visit', {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {
    // Intentionally swallow — analytics must NEVER break the UX.
  });
}

// ─── PR2 §2.2 — getRecentVisits for the FOMO wall polling hook ──────────────
//
// Per the controller §4 spec note ("NO necesita cambios si getHubCrmData ya
// devuelve los nuevos campos"), the backend does NOT expose a dedicated
// `/recent-visits` endpoint. The polling hook (use-recent-visits.ts) calls
// this function, which delegates to `getHubCrmData('30d')` + extracts the
// `.recentVisits` field.
//
// Why this is acceptable for the 30s polling cadence (Founder decision §5.8
// "batch polling every 30s" — explicitly NOT real-time Redis/websocket):
//   - The dashboard's full aggregate query is ~9 sub-queries parallelized
//     in 1 RTT (see backend getHubCrmData Promise.all). At a 30s cadence
//     the marginal cost is 2 req/min — well within the user's auth budget.
//   - The alternative (a separate lightweight /recent-visits endpoint)
//     would require a controller change (spec explicitly forbids it).
//   - The 30s cadence is intentionally coarse — the FOMO wall is a "social
//     proof" surface, not a real-time dashboard. Batching tolerates the
//     extra bandwidth + the dashboard aggregate benefits from caching
//     opportunities at the CDN layer (the response is JWT-auth'd so it's
//     per-user cacheable by Bunny edge for the 30s window).
//
// `limit` is accepted as a parameter for API symmetry with the backend
// `getRecentVisits(userId, limit)` service function, but is FIXED at 5 by
// the backend's getHubCrmData aggregation (per Founder decision §5.5 — the
// FOMO wall always shows the last 5 visitors). The parameter is documented
// here so a future iteration that introduces a dedicated endpoint can
// honor the limit without breaking the hook's call site.

/**
 * Fetch the most recent Hub visits for the FOMO wall polling hook.
 *
 * @param limit FIXED at 5 by the backend (per Founder decision §5.5).
 *              Parameter accepted for API symmetry + forward compatibility
 *              with a future dedicated /recent-visits endpoint.
 * @returns array of { visitedAt, referer, ctaClicked } most-recent-first.
 *          visitedAt is an ISO 8601 string (Express/JSON serializes Date → string).
 */
export async function getRecentVisits(
  limit = 5,
): Promise<HubCrmRecentVisit[]> {
  // Delegate to getHubCrmData (no dedicated backend endpoint per controller
  // §4 spec note). The `limit` parameter is ignored on the wire — the
  // backend always returns the last 5. Documented above for forward compat.
  void limit;
  const data = await getHubCrmData('30d');
  return data.recentVisits;
}
